// eslint-disable-next-line import/no-extraneous-dependencies
import { HTMLElement, parse } from 'node-html-parser';


/*
 * ### Conversions currently supported:
 * - `ptpx` → `pt`
 * - `px` → `pt` (1px = 0.75pt)
 * - `em` → `pt` (1em = 12pt)
 * - `rem` → `pt` (1rem = 12pt)
 * - Viewport units (`vw`, `vh`, `vmin`, `vmax`) → Default to `12pt`
 */
const FONT_SIZE_CONVERSIONS = {
  ptpx: (size: number) => `${size}pt`,
  px: (size: number) => `${(size * 0.75).toFixed(2)}pt`,
  em: (size: number) => `${(size * 12).toFixed(2)}pt`,
  rem: (size: number) => `${(size * 12).toFixed(2)}pt`,
  vw: () => '12pt',
  vh: () => '12pt',
  vmin: () => '12pt',
  vmax: () => '12pt',
};


/**
 * Sanitizes an HTML string for PDF rendering. This function parses the provided
 * HTML string, traverses its elements, and updates the style or element attributes
 * accordingly. Additionally, this function can still be improved upon to handle
 * other incompatible or problematic HTML or CSS code that affects PDF rendering.
 *
 * @param {string} htmlString - The HTML string to sanitize.
 * @returns {string} - The sanitized HTML string with updated styles.
 */
export const sanitizeHTMLStringForPDFRender = (htmlString: string): string => {
  const parsed = parse(htmlString);

  /*
  * Currently, this function only addresses incompatible or problematic CSS units
  * in inline styles to PDF-compatible units. This is done by adjusting the `style`
  * attribute to ensure compatibility with PDF rendering. It specifically handles
  * font-size conversions for units like `ptpx`, `px`, `em`, `rem`, and viewport
  * units (`vw`, `vh`, etc.).
  */
  const sanitizeStyle = (style: string): string => {
    const fontSizeRegex = new RegExp(
      `font-size:\\s*(\\d+(?:\\.\\d+)?)(?:\\s*(${Object.keys(FONT_SIZE_CONVERSIONS).join('|') }))(;|$)`,
      'gi',
    );

    return style.replace(fontSizeRegex, (match, size, unit, delimiter) => {
      const convert = FONT_SIZE_CONVERSIONS[unit as keyof typeof FONT_SIZE_CONVERSIONS];
      return convert ? `font-size: ${convert(parseFloat(size))}${delimiter}` : match;
    });
  };

  const traverse = (node: HTMLElement) => {
    if (node.nodeType === 1) { // 1 is an "Element" node type
      if (node.hasAttribute('style')) {
        const style = node.getAttribute('style');

        if (style) {
          const updatedStyle = sanitizeStyle(style);

          if (style !== updatedStyle) {
            node.setAttribute('style', updatedStyle);
          }
        }
      }

      node.childNodes.forEach((child) => {
        if (child instanceof HTMLElement) {
          traverse(child);
        }
      });
    }
  };

  traverse(parsed);
  return parsed.toString();
};
